import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';

// Real revenue/retention/payback tracking per commissioned sale — built from
// the method proven live 9/25/26 (Josh's May-Aug ROI analysis): pull each
// sale's real PlayByPoint payment history (not COMS's own daily-synced copy,
// which has a confirmed never-expires bug on valid_status) and compare it to
// what was actually paid in commission and payroll. Refreshed nightly by
// refreshCommissionOutcomes in queries.js — see commission_sale_outcomes.
//
// Scope note: this shows CURRENT cumulative totals per sale/cohort/rep, not
// a full calendar-month revenue-recognition waterfall (the DB stores one
// running total per sale, not each individual payment) — that fuller view
// stays a periodic manual export for now (see the 9/25/26 ROI spreadsheet).

function fmtMoney(n) { return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }
function fmtMultiple(n) { return isFinite(n) && n > 0 ? `${n.toFixed(2)}x` : '—'; }

// Weekly loaded-cost estimate from a rep's payroll settings: base salary,
// grossed up for employer Social Security/Medicare + workers' comp.
function weeklyPayrollCost(payrollSettings) {
  if (!payrollSettings?.baseSalary) return 0;
  const { baseSalary, employerTaxRate, workersCompRate } = payrollSettings;
  const loaded = baseSalary * (1 + Number(employerTaxRate || 0) + Number(workersCompRate || 0));
  return loaded / 52;
}

function cumulativePayrollCost(payrollSettings, asOf = new Date()) {
  if (!payrollSettings?.employmentStartDate) return 0;
  const start = new Date(payrollSettings.employmentStartDate);
  const days = Math.max(0, (asOf - start) / (1000 * 60 * 60 * 24));
  return (days / 7) * weeklyPayrollCost(payrollSettings);
}

// A sale counts as "retained" if the member is currently active, or was
// cancelled but their real membership_end date is still in the future (i.e.
// they're paid through a period that hasn't ended yet).
function isRetained(row, asOf = new Date()) {
  if (row.current_status === 'active') return true;
  if (row.current_status === 'cancelled' && row.membership_end) {
    return new Date(row.membership_end) >= asOf;
  }
  return false;
}

function groupByCohortMonth(rows) {
  const byMonth = new Map();
  for (const r of rows) {
    const month = (r.sale_date || '').slice(0, 7);
    if (!byMonth.has(month)) byMonth.set(month, []);
    byMonth.get(month).push(r);
  }
  return [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export default function CommissionRoi() {
  const { get, post } = useApi();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    get('/commission/roi-summary').then(setRows).catch(console.error).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleRefreshNow() {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      // A real run pulls the full 6-club customer + payment history and takes
      // several minutes — fire-and-forget, then reload once it resolves.
      const summary = await post('/commission/roi-refresh', {});
      setRefreshMsg(`Refreshed ${summary.salesUpdated} of ${summary.salesConsidered} sales${summary.errors?.length ? ` (${summary.errors.length} errors — check logs)` : ''}.`);
      load();
    } catch (e) {
      setRefreshMsg(`Refresh failed: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!rows) return null;

  const asOf = new Date();
  const totalCommission = rows.reduce((s, r) => s + Number(r.commission_amount || 0), 0);
  const totalRevenue = rows.reduce((s, r) => s + Number(r.real_revenue_collected || 0), 0);
  const retainedCount = rows.filter(r => isRetained(r, asOf)).length;
  const retentionPct = rows.length ? retainedCount / rows.length : 0;

  // Payroll: sum unique reps' cumulative cost (not per-sale — payroll is per rep, not per sale)
  const repIds = [...new Set(rows.map(r => r.rep_user_id))];
  const payrollByRep = new Map(repIds.map(id => {
    const sample = rows.find(r => r.rep_user_id === id);
    return [id, sample?.payrollSettings || null];
  }));
  const totalPayroll = repIds.reduce((s, id) => s + cumulativePayrollCost(payrollByRep.get(id), asOf), 0);
  const anyPayrollConfigured = [...payrollByRep.values()].some(Boolean);

  const cohorts = groupByCohortMonth(rows);
  const lastRefreshed = rows.map(r => r.last_refreshed_at).filter(Boolean).sort().slice(-1)[0];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {lastRefreshed ? `Real revenue last refreshed from PlayByPoint: ${new Date(lastRefreshed).toLocaleString()}` : 'Not yet refreshed — run it once to populate real revenue.'}
        </div>
        <button className="btn btn-ghost btn-sm" disabled={refreshing} onClick={handleRefreshNow}>
          {refreshing ? 'Refreshing (this takes a few minutes)…' : '↻ Refresh Now'}
        </button>
      </div>
      {refreshMsg && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>{refreshMsg}</div>}

      <div style={styles.kpiBar}>
        <div style={styles.kpiItem}><div style={styles.kpiValue}>{fmtMoney(totalCommission)}</div><div style={styles.kpiLabel}>Commission Paid</div></div>
        <div style={styles.kpiItem}><div style={{ ...styles.kpiValue, color: 'var(--green)' }}>{fmtMoney(totalRevenue)}</div><div style={styles.kpiLabel}>Real Revenue Collected</div></div>
        <div style={styles.kpiItem}><div style={styles.kpiValue}>{fmtMultiple(totalCommission ? totalRevenue / totalCommission : 0)}</div><div style={styles.kpiLabel}>Payback (Commission)</div></div>
        <div style={styles.kpiItem}><div style={styles.kpiValue}>{Math.round(retentionPct * 100)}%</div><div style={styles.kpiLabel}>Retention</div></div>
        {anyPayrollConfigured && (
          <>
            <div style={styles.kpiItem}><div style={{ ...styles.kpiValue, color: 'var(--red)' }}>{fmtMoney(totalPayroll)}</div><div style={styles.kpiLabel}>Payroll Cost (est.)</div></div>
            <div style={styles.kpiItem}><div style={styles.kpiValue}>{fmtMultiple(totalPayroll ? totalRevenue / totalPayroll : 0)}</div><div style={styles.kpiLabel}>Payback (Payroll)</div></div>
          </>
        )}
      </div>

      {!anyPayrollConfigured && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          No payroll figures set for any rep yet — Payback (Payroll) will appear once base salary is entered in Commission Rates for a rep.
        </div>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Cohort Month</th>
              <th style={styles.th}>Sales</th>
              <th style={styles.th}>Retention</th>
              <th style={styles.th}>Commission Paid</th>
              <th style={styles.th}>Real Revenue Collected</th>
              <th style={styles.th}>Payback Multiple</th>
            </tr>
          </thead>
          <tbody>
            {cohorts.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>No commission sales yet</td></tr>
            )}
            {cohorts.map(([month, cohortRows]) => {
              const commission = cohortRows.reduce((s, r) => s + Number(r.commission_amount || 0), 0);
              const revenue = cohortRows.reduce((s, r) => s + Number(r.real_revenue_collected || 0), 0);
              const retained = cohortRows.filter(r => isRetained(r, asOf)).length;
              return (
                <tr key={month} className="table-row-hover">
                  <td style={{ ...styles.td, fontWeight: 700 }}>{month}</td>
                  <td style={styles.td}>{cohortRows.length}</td>
                  <td style={styles.td}>{Math.round(100 * retained / cohortRows.length)}%</td>
                  <td style={styles.td}>{fmtMoney(commission)}</td>
                  <td style={{ ...styles.td, color: 'var(--green)' }}>{fmtMoney(revenue)}</td>
                  <td style={{ ...styles.td, fontWeight: 700 }}>{fmtMultiple(commission ? revenue / commission : 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  kpiBar: {
    display: 'flex', gap: 0, background: '#141414', border: '1px solid var(--border)',
    borderRadius: 8, overflow: 'hidden', marginBottom: '1rem', flexWrap: 'wrap',
  },
  kpiItem: { flex: 1, padding: '0.6rem 0.75rem', borderRight: '1px solid var(--border)', minWidth: 130, textAlign: 'center' },
  kpiValue: { fontSize: '1.15rem', fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, fontFamily: 'DM Mono, monospace' },
  kpiLabel: { fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: '0.15rem', whiteSpace: 'nowrap' },
  tableWrapper: { overflowX: 'auto', background: '#141414', border: '1px solid var(--border)', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem', minWidth: 700 },
  th: {
    padding: '0.55rem 0.85rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600,
    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid var(--border)', background: '#141414', whiteSpace: 'nowrap',
  },
  td: { padding: '0.55rem 0.85rem', borderBottom: '1px solid var(--border)', color: 'var(--text)', whiteSpace: 'nowrap' },
};
